import { BasePagination, HStack, IconButton, Tooltip, useDisclosure } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { IErpIntegrationData, useGetErpIntegrationsApi } from 'apis/erp-mappings/use-get-erp-integrations-api';
import { BaseTable, BaseTableContainer } from 'components/base-table';
import { BaseTableSpinner, BaseTableTitle, CustomTableCell, TableHeader } from 'components/base-table/components';
import { BaseSortButton } from 'components/base-table/sorting';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { FiPlus, FiX } from 'react-icons/fi';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import {
  BranchMappingEditModal,
  CreateIntegrationModal,
  ErpActionsCell,
  ErpRestaurantCell,
  ErpSupplierCell,
  FilterableTableHeader,
  TOption,
} from '../components';

const ErpMappingsListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isCreateModalOpen, onOpen: onCreateModalOpen, onClose: onCreateModalClose } = useDisclosure();
  const [selectedMapping, setSelectedMapping] = useState<{
    restaurantId: number;
    supplierId: number;
    supplierType: string;
  } | null>(null);

  // Filter state
  const [restaurantFilter, setRestaurantFilter] = useState<TOption | null>(null);
  const [supplierFilter, setSupplierFilter] = useState<TOption | null>(null);

  const { isLoading, isFetching, data } = useGetErpIntegrationsApi({
    restaurantId: restaurantFilter?.value as number,
    supplierId: supplierFilter?.value as number,
  });
  const { pageCount, total, data: erpData } = data ?? {};

  // Filter load functions
  const loadRestaurantFilterOptions = (inputValue: string, callback: any) => {
    http
      .get('restaurants', {
        params: {
          page: 1,
          limit: 20,
          filter: inputValue ? `nameEn||$contL||${inputValue}` : undefined,
        },
      })
      .then((res) => {
        const restaurants = res?.data?.data || [];
        const options: TOption[] = [];

        restaurants.forEach((item: any) => {
          options.push({ value: item?.id, label: item?.name });
        });

        callback(options);
      })
      .catch(() => {
        callback([]);
      });
  };

  const loadSupplierFilterOptions = (inputValue: string, callback: any) => {
    http
      .get('suppliers', {
        params: {
          page: 1,
          limit: 20,
          filter: inputValue ? `name||$contL||${inputValue}` : undefined,
        },
      })
      .then((res) => {
        const suppliers = res?.data?.data || [];
        const options: TOption[] = [];

        suppliers.forEach((item: any) => {
          options.push({ value: item?.id, label: item?.name });
        });

        callback(options);
      })
      .catch(() => {
        callback([]);
      });
  };

  const handleEditMappings = (restaurantId: number, supplierId: number, supplierType: string) => {
    setSelectedMapping({ restaurantId, supplierId, supplierType });
    onEditModalOpen();
  };

  const handleCloseEditModal = () => {
    setSelectedMapping(null);
    onEditModalClose();
  };

  useEffect(() => {
    dispatch(changeTitle('ERP Mappings'));
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <FilterableTableHeader
            name='Restaurant'
            sortButtonComponent={<BaseSortButton sortBy='restaurant.name' />}
            filterValue={restaurantFilter}
            onFilterChange={setRestaurantFilter}
            loadOptions={loadRestaurantFilterOptions}
            placeholder='Filter by Restaurant'
          />
        ),
        accessor: 'restaurant',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['restaurant']>) => (
          <CustomTableCell<{ name: string; id: number }> as={ErpRestaurantCell} name={value.name} id={value.id} />
        ),
      },
      {
        Header: (
          <FilterableTableHeader
            name='Supplier'
            sortButtonComponent={<BaseSortButton sortBy='supplier.name' />}
            filterValue={supplierFilter}
            onFilterChange={setSupplierFilter}
            loadOptions={loadSupplierFilterOptions}
            placeholder='Filter by Supplier'
          />
        ),
        accessor: 'supplier',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['supplier']>) => (
          <CustomTableCell<{ name: string; id: number; type: string }>
            as={ErpSupplierCell}
            name={value.name}
            id={value.id}
            type={value.type}
          />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <CustomTableCell<{
            restaurantId: number;
            supplierId: number;
            supplierType: string;
            onEditMappings: (restaurantId: number, supplierId: number, supplierType: string) => void;
          }>
            as={ErpActionsCell}
            restaurantId={value.restaurantId}
            supplierId={value.supplierId}
            supplierType={value.supplierType}
            onEditMappings={handleEditMappings}
          />
        ),
      },
    ],
    [restaurantFilter, supplierFilter, loadRestaurantFilterOptions, loadSupplierFilterOptions]
  );

  interface IColumnsAccessorData {
    id: string;
    restaurant: {
      id: number;
      name: string;
    };
    supplier: {
      id: number;
      name: string;
      type: string;
    };
    actions: {
      restaurantId: number;
      supplierId: number;
      supplierType: string;
    };
  }

  const columnsAccessorData = React.useMemo(
    () =>
      erpData?.map((item: IErpIntegrationData): IColumnsAccessorData => {
        return {
          id: `${item.restaurant.id}-${item.supplier.id}`,
          restaurant: {
            id: item.restaurant.id,
            name: item.restaurant.name,
          },
          supplier: {
            id: item.supplier.id,
            name: item.supplier.name,
            type: item.supplier.type,
          },
          actions: {
            restaurantId: item.restaurant.id,
            supplierId: item.supplier.id,
            supplierType: item.supplier.type,
          },
        };
      }),
    [erpData]
  );

  return (
    <>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <BaseTableTitle title='ERP Mappings' />
            <BaseTableSpinner isFetching={isFetching} />
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <HStack spacing={3}>
              <Tooltip label='Create Integration'>
                <IconButton
                  aria-label='Create integration'
                  icon={<FiPlus />}
                  colorScheme='green'
                  onClick={onCreateModalOpen}
                />
              </Tooltip>
              <Tooltip label='Clear Filters'>
                <IconButton
                  aria-label='Clear filters'
                  icon={<FiX />}
                  variant='outline'
                  colorScheme='gray'
                  onClick={() => {
                    setRestaurantFilter(null);
                    setSupplierFilter(null);
                  }}
                  isDisabled={!restaurantFilter && !supplierFilter}
                />
              </Tooltip>
            </HStack>
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable columns={columns} data={columnsAccessorData} />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>

      {selectedMapping && (
        <BranchMappingEditModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          restaurantId={selectedMapping.restaurantId}
          supplierId={selectedMapping.supplierId}
          supplierType={selectedMapping.supplierType}
        />
      )}

      <CreateIntegrationModal isOpen={isCreateModalOpen} onClose={onCreateModalClose} />
    </>
  );
};

export default ErpMappingsListScreen;
